/**
 * Dimensions for the payment-collection workflows (`order_payment_record`,
 * `delivery_settlement`). Only enums and booleans: no amount, reference text,
 * customer or order identity ever leaves the browser. Contract:
 * `docs/analytics/payment-collection-ux.md`.
 */
export type PaymentEntryPoint =
    'record_dialog' | 'mark_paid' | 'dashboard' | 'trip' | 'order_detail'
export type PaymentMethodChoice = 'cash' | 'bank_transfer' | 'credit'
export type AmountChoice = 'default' | 'edited_lower' | 'edited_other'

export interface PaymentCollectionContext {
    entryPoint?: PaymentEntryPoint
    paymentMethod?: PaymentMethodChoice | (string & {})
    /** A reference was typed; its text is never sent. */
    hasReference?: boolean
    amountChoice?: AmountChoice
    /** Driver's settlement intent on the order when the dialog opened. */
    settlementIntent?: string | null
    paymentStatus?: string
    /** Cash collected on delivery is less than what the order still owed. */
    cashPartial?: boolean
    collectFee?: boolean
    feeCollectible?: boolean
}

const ENTRY_POINTS: readonly string[] = [
    'record_dialog',
    'mark_paid',
    'dashboard',
    'trip',
    'order_detail',
]
const METHODS: readonly string[] = ['cash', 'bank_transfer', 'credit']
const AMOUNT_CHOICES: readonly string[] = [
    'default',
    'edited_lower',
    'edited_other',
]
const INTENTS: readonly string[] = [
    'cash',
    'bank_transfer',
    'credit',
    'counter',
]
const PAYMENT_STATUSES: readonly string[] = ['unpaid', 'partial', 'paid']
const BOOLEAN_KEYS = [
    ['hasReference', 'has_reference'],
    ['cashPartial', 'cash_partial'],
    ['collectFee', 'collect_fee'],
    ['feeCollectible', 'fee_collectible'],
] as const

/** Values outside the enums are dropped rather than widening the contract. */
export function paymentCollectionContextProps(
    metadata: PaymentCollectionContext,
): Record<string, unknown> {
    const props: Record<string, unknown> = {}
    if (ENTRY_POINTS.includes(metadata.entryPoint ?? ''))
        props.entry_point = metadata.entryPoint
    if (METHODS.includes(metadata.paymentMethod ?? ''))
        props.payment_method = metadata.paymentMethod
    if (AMOUNT_CHOICES.includes(metadata.amountChoice ?? ''))
        props.amount_choice = metadata.amountChoice
    if (metadata.settlementIntent !== undefined)
        props.settlement_intent = INTENTS.includes(
            metadata.settlementIntent ?? '',
        )
            ? metadata.settlementIntent
            : 'none'
    if (PAYMENT_STATUSES.includes(metadata.paymentStatus ?? ''))
        props.payment_status = metadata.paymentStatus
    for (const [key, prop] of BOOLEAN_KEYS)
        if (typeof metadata[key] === 'boolean') props[prop] = metadata[key]
    return props
}

/**
 * How the typed amount relates to the prefilled remaining debt. Equal is
 * `default`; below is `edited_lower`; anything else (above, zero, empty) is
 * `edited_other`.
 */
export function amountChoice(
    amount: unknown,
    defaultAmount: number,
): AmountChoice {
    const value =
        typeof amount === 'number' ? amount : Number(String(amount ?? ''))
    if (!Number.isFinite(value) || String(amount ?? '').trim() === '')
        return 'edited_other'
    if (value === defaultAmount) return 'default'
    return value > 0 && value < defaultAmount ? 'edited_lower' : 'edited_other'
}

const ERROR_CODE = /^[A-Z][A-Z0-9_]{0,63}$/

/**
 * Server error codes are fixed upper-snake constants (`OVERPAYMENT`); anything
 * else, including a raw message, collapses to `UNKNOWN_ERROR`.
 */
export function workflowErrorCode(error: unknown): string {
    const code = (error as { code?: unknown } | null | undefined)?.code
    return typeof code === 'string' && ERROR_CODE.test(code)
        ? code
        : 'UNKNOWN_ERROR'
}

/**
 * Calls `send` exactly once. `track` wraps it for analytics (normally the
 * workflow `run`); if tracking throws or never reaches `send`, the business
 * call still goes out. Rejections are swallowed: the mutation's own error
 * handler has already told the user.
 */
export function sendOnceTracked(
    track: (execute: () => Promise<unknown>) => unknown,
    send: () => unknown,
): void {
    let sent = false
    const deliver = (): Promise<unknown> => {
        sent = true
        try {
            return Promise.resolve(send())
        } catch (error) {
            return Promise.reject(error)
        }
    }
    let tracked: unknown
    try {
        tracked = track(deliver)
    } catch {
        /* Analytics cannot affect the business operation. */
    }
    if (!sent) tracked = deliver()
    void Promise.resolve(tracked).catch(() => {})
}
